<template>
  <div>
    <gmap-map
      ref="googleMapRef"
      :center="{ lat: 10, lng: 10 }"
      :zoom="zoomLevel"
      :options="{
        streetViewControl: false,
        rotateControl: false,
        fullscreenControlOptions: {
          position: 2,
        },
        mapTypeControlOptions: {
          position: 2,
        },
        mapId: vectorMapId,
      }"
      map-type-id="roadmap"
      style="height: 100vh"
    >
      <!-- ======================== -->
      <!-- Back to Normal FAB       -->
      <!-- ======================== -->
      <v-tooltip right>
        <template v-slot:activator="{ on }">
          <v-btn fab dark @click="switchToNormal()" class="ma-4" color="grey darken-3" v-on="on">
            <v-icon large>mdi-earth</v-icon>
          </v-btn>
        </template>
        <span class="aid-logotype">AgriBus-Web</span>
      </v-tooltip>

      <!-- ======================== -->
      <!-- Tractor FAB              -->
      <!-- ======================== -->
      <v-speed-dial v-model="tractorSpeedDial" top right absolute direction="bottom" transition="slide-y-transition">
        <template v-slot:activator>
          <v-btn fab dark color="grey darken-3">
            <v-badge v-model="activeTractorExists" color="error">
              <template v-slot:badge>
                <span>{{ realtimeActiveDeviceIds.length }}</span>
              </template>
              <v-icon large>mdi-tractor</v-icon>
            </v-badge>
          </v-btn>
        </template>

        <v-list
          class="device-list"
          dark
          color="rgba(0, 0, 0, 0.4)"
          :min-width="vehicleIndicatorWindowWidth"
          :style="vehicleIndicatorWindowStyle"
        >
          <v-subheader>設定</v-subheader>

          <v-list-item @click.stop="isShowWorkDetail = true">
            <v-list-item-avatar class="mr-1">
              <v-icon :color="autoTrackingIconColor">trending_up</v-icon>
            </v-list-item-avatar>
            <v-list-item-content :class="autoTrackingTextColor">
              <v-list-item-title>
                自動追尾
              </v-list-item-title>
            </v-list-item-content>
            <v-switch flat color="lime accent-1" v-model="autoTracking" />
          </v-list-item>

          <v-divider></v-divider>
          <v-subheader>マシン</v-subheader>
          <v-list-item
            v-for="(device, i) in allDevices"
            :key="i"
            @click.stop="
              isShowWorkDetail = true
              targetDevice = device
            "
            :class="isActive(device.device) ? 'online' : 'offline'"
            class="connect-status-border"
          >
            <v-list-item-avatar class="mr-1">
              <user-icon-component
                size="24"
                :showNickname="true"
                :iconUrl="getAccount(device.device).iconUrl"
                :nickname="getAccount(device.device).nickname"
              />
            </v-list-item-avatar>

            <v-list-item-content :class="isActive(device.device) ? 'white--text' : 'grey--text'">
              <v-list-item-title>
                <span class="d-inline-block text-truncate" style="max-width: 220px">
                  {{ getMarkerName(device.device) }}
                </span>
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-avatar class="mx-0 px-0" @click="openWebMarkerDialog(device.device)">
              <v-img :src="getMarkerArrowSrc(device.device)" max-width="24" max-height="24" />
            </v-list-item-avatar>
            <v-list-item-avatar class="mr-1 ml-0">
              <v-switch
                flat
                v-model="device.monitor"
                color="yellow lighten-3"
                @change="onDeviceMonitorChanged(device.device, $event)"
              />
            </v-list-item-avatar>
          </v-list-item>
        </v-list>
      </v-speed-dial>

      <!-- ======================== -->
      <!-- 詳細表示パネル             -->
      <!-- ======================== -->
      <v-speed-dial bottom left absolute v-if="isShowWorkDetail && targetDevice != null && targetDevice.monitor">
        <template v-slot:activator>
          <v-card width="250" style="margin-bottom: 20px" dark color="rgba(0, 0, 0, 0.4)">
            <br />
            <v-card-text class="pt-1 pb-0 px-1">
              <span class="d-inline-block text-truncate" style="max-width: 235px;">
                <v-avatar>
                  <user-icon-component
                    size="24"
                    :showNickname="true"
                    :iconUrl="getAccount(targetDevice.device).iconUrl"
                    :nickname="getAccount(targetDevice.device).nickname"
                  />
                </v-avatar>
                {{ getMarkerName(targetDevice.device) }}
              </span>
            </v-card-text>
            <v-card-text v-if="isActive(targetDevice.device)" class="ma-0 pt-2">
              <div v-if="targetNaviRealtimeInfo">
                <dl class="info-panel-details">
                  <dt>{{ $tt('customWorkWidth') }}：</dt>
                  <dd>
                    {{ targetNaviRealtimeInfo.getWorkWidthForDisplay() }}
                  </dd>
                  <dt>{{ $tt('customSpeed') }}：</dt>
                  <dd>
                    {{ targetNaviRealtimeInfo.getSpeedForDisplay() }}
                  </dd>
                  <dt>{{ $tt('customHeight') }}：</dt>
                  <dd>
                    {{ targetNaviRealtimeInfo.getHeightForDisplay() }}
                  </dd>
                  <dt>{{ $tt('customNowProgressRateTitle') }}：</dt>
                  <dd>
                    {{ targetNaviRealtimeInfo.getCoveredForDisplay() }}
                  </dd>
                </dl>
              </div>
            </v-card-text>
            <v-card-text v-else>
              Not Active
            </v-card-text>
            <v-btn icon absolute top right x-small @click="isShowWorkDetail = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card>
        </template>
      </v-speed-dial>

      <!-- ======================== -->
      <!-- WebMarker編集ダイアログ    -->
      <!-- ======================== -->
      <v-dialog v-model="showEditWebMarkerDialog" persistent max-width="50%" dark>
        <v-card flat tile>
          <v-card-title class="headline">
            {{ $tt('customWebMarkerSetting') }}
          </v-card-title>
          <v-card-text>
            <v-text-field v-model="currentWebMarkerTrackName" :label="$tt('customWebMarkerName')" maxlength="128">
              {{ currentWebMarkerTrackName }}
            </v-text-field>
          </v-card-text>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                {{ $tt('customWebMarkerColor') }}
                <v-color-picker
                  v-model="currentWebMarkerTrackColor"
                  hide-canvas
                  hide-inputs
                  show-swatches
                  flat
                  width="100%"
                  swatches-max-height="70"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="green darken-1" text @click="closeWebMarkerDialog()">
              {{ $tt('customCancel') }}
            </v-btn>
            <v-btn color="green darken-1" text @click="saveWebMarker()">
              {{ $tt('customSaveAction') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <GmapMarker :key="nowMarker.label" v-for="nowMarker in nowMarkers" :position="nowMarker.position"
      :icon="nowMarker.icon" :visible="nowMarker.visible" optimized: false />

    </gmap-map>
  </div>
</template>

<script>
import MapMixin from '@/components/mixins/map-mixin'
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import NowMixin from '@/components/mixins/now-mixin'
import Account from '@/lib/account'
import * as Constants from '@/lib/constants'
import DevicesInfo from '@/lib/devices-info'
import MarkerArrow from '@/lib/mapbox/marker-arrow'
import UserInfo from '@/lib/user-info'
import WebMarkersInfo from '@/lib/web-markers-info'
import store from '@/store'
import Vue from 'vue'
import UserIconComponent from '@/components/user/UserIconComponent'
import { identifier } from '@babel/types'

const STYLE_TEXT_WHITE = 'white--text'
const STYLE_TEXT_GREY = 'grey--text'
const STYLE_ACTIVE_COLOR = 'lime accent-1'
const STYLE_INACTIVE_COLOR = 'blue-grey lighten-5'

const DEFAULT_ZOOM_LEVEL = 18
const REALTIME_INFO_REFRESH_INTERVAL_MILLISECONDS = 1000

const VEHICLE_INDICATOR_WINDOW_WIDTH = 400
const VEHICLE_INDICATOR_WINDOW_LEFT_OFFSET = (VEHICLE_INDICATOR_WINDOW_WIDTH / 2 - 30) * -1

export default {
  name: 'NowNow',
  components: {
    UserIconComponent,
  },
  mixins: [MapMixin, NowMixin, GoogleMapMixin],

  computed: {
    currentMapCenter() {
      return this.$store.getters.currentMapCenter
    },
    zoomLevel() {
      return DEFAULT_ZOOM_LEVEL
    },
    isImpersonating() {
      return UserInfo.isImpersonating()
    },
    vehicleIndicatorWindowWidth() {
      return VEHICLE_INDICATOR_WINDOW_WIDTH
    },
    vehicleIndicatorWindowStyle() {
      return {
        position: 'relative',
        left: VEHICLE_INDICATOR_WINDOW_LEFT_OFFSET + 'px',
      }
    },
  },

  data() {
    return {
      activeTimer: undefined,
      realTimeEventBus: new Vue(),

      tractorSpeedDial: false,

      currentWebMarkerDevice: undefined,
      currentWebMarkerTrackColor: undefined,
      currentWebMarkerTrackName: undefined,

      isShowWorkDetail: false,
      targetDevice: undefined,

      targetNaviRealtimeInfo: undefined,

      showEditWebMarkerDialog: false,

      autoTracking: false,
      autoTrackingTextColor: STYLE_TEXT_GREY,
      autoTrackingIconColor: STYLE_INACTIVE_COLOR,
    }
  },

  mounted() {
    // JAめむろSAC対応
    if (UserInfo.getUsername() === '0000000000162553') {
      this.fieldFillColor = null
      this.fieldOpacity = 0
    }

    this.$store.dispatch('setNowShowingRealtime', true)
    this.initDeviceMarkers()
    this.activateRefreshDaemon()
    this.connectMqtt()
    this.updateAllDevices()

    this.$store.dispatch('setFieldGeoJsons', { type: 'FeatureCollection', features: [] })
    const params = {
      onSuccess: this.initMap,
    }
    this.$store.dispatch('getAllFields', params)

    // MQTTでメッセージを受けた時のイベントハンドラのセットアップ
    this.setUpShowRealtimeEventHandler(this.realTimeEventBus, this.$store)
    this.setUpShowLatestTrackEventHandler(this.realTimeEventBus, this.$store)
  },

  beforeDestroy() {
    this.$store.dispatch('setNowShowingRealtime', false)
    if (this.mqtt) {
// TEMP       console.debug('destroy mqtt')
      this.mqtt.end()
    }
    if (this.activeTimer) {
      clearInterval(this.activeTimer)
    }
  },

  methods: {
    initMap() {
      const fillColor = this.fieldFillColor
      const fillOpacity = this.fieldOpacity

      this.drawAllFieldsOnGoogleMap()
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setTilt(0)
        map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
        let bounds = new google.maps.LatLngBounds()
        map.data.forEach(feature => {
          const type = feature.getProperty(Constants.GeoFeaturePropertiesType.TYPE)

          feature.getGeometry().forEachLatLng(latlng => {
            bounds.extend(latlng)
          })
        })

        map.data.setStyle(feature => {
          const type = feature.getProperty(Constants.GeoFeaturePropertiesType.TYPE)

          if (type === Constants.GeoFeaturePropertiesType.FIELD) {
            return {
              fillColor: fillColor,
              fillOpacity: fillOpacity,
              strokeColor: Constants.FIELD_FILL_COLOR,
              strokeWeight: 1,
              zIndex: 0,
            }
          }
        })

        map.fitBounds(bounds)
      })
    },
    getAppBarColor() {
      if (this.$vuetify.theme.dark) {
        return ''
      }
      return Constants.CONNECT_GREEN
    },

    switchToNormal() {
      this.nowDrawer = false
      this.$store.dispatch('setNowShowingRealtime', false)
      this.$router.push({ name: 'FieldListAsList' })
    },

    getMarkerArrowSrc(device) {
      const arrow = new MarkerArrow()
      const trackColor = WebMarkersInfo.getTrackColor(device.username, device.id)
      return arrow.getSvgImgSrc(trackColor)
    },

    getMarkerName(device) {
      const trackName = WebMarkersInfo.getTrackName(device.username, device.id)
      if (!trackName) {
        return device.manufacturer + ' ' + device.model
      }
      return trackName
    },

    getAccount(device) {
      return Account.getByDevice(device)
    },

    activateRefreshDaemon() {
      const devices = DevicesInfo.devices()
      const store = this.$store
      // const onRealtimeInfoUpdated = this.onRealtimeInfoUpdated
      // リアルタイム情報のリフレッシュタイマー
      if (!this.activeTimer) {
        this.activeTimer = setInterval(() => {
          store.dispatch('refreshRealtimeInfoActivities')
        }, REALTIME_INFO_REFRESH_INTERVAL_MILLISECONDS)
      }
    },

    openWebMarkerDialog(device) {
      this.currentWebMarkerDevice = device
      this.currentWebMarkerTrackColor = WebMarkersInfo.getTrackColor(device.username, device.id)
      this.currentWebMarkerTrackName = WebMarkersInfo.getTrackName(device.username, device.id)
      this.showEditWebMarkerDialog = true
    },

    closeWebMarkerDialog() {
      this.showEditWebMarkerDialog = false
    },

    saveWebMarker() {
      const device = this.currentWebMarkerDevice
      const trackColor = this.currentWebMarkerTrackColor
      const trackName = this.currentWebMarkerTrackName
      this.updateWebMarker(device, trackColor, trackName)
      this.closeWebMarkerDialog()
    },
  },
  watch: {
    naviRealtimeInfo() {
      if (!this.targetDevice) {
        return
      }
      const deviceInfoIdentifier = DevicesInfo.getIdentifier(
        this.targetDevice.device.username,
        this.targetDevice.device.id
      )
      const realTimeInfoIdentifier = DevicesInfo.getIdentifier(
        this.naviRealtimeInfo.getUsername(),
        this.naviRealtimeInfo.getDeviceId()
      )
      if (realTimeInfoIdentifier === deviceInfoIdentifier) {
        this.targetNaviRealtimeInfo = this.naviRealtimeInfo
      }
    },
    autoTracking() {
      if (this.autoTracking) {
        this.$store.dispatch('setNowAutoTracking', true)
        this.autoTrackingTextColor = STYLE_TEXT_WHITE
        this.autoTrackingIconColor = STYLE_ACTIVE_COLOR
      } else {
        this.$store.dispatch('setNowAutoTracking', false)
        this.autoTrackingTextColor = STYLE_TEXT_WHITE
        this.autoTrackingIconColor = STYLE_INACTIVE_COLOR
      }
    },
  },
}
</script>

<style>
.vehicle {
  display: block;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  margin: auto;
}

.vehicleCaption {
  color: white;
  font-size: xx-small;
  background-color: black;
  z-index: 1000;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 1em;
  padding-right: 1em;
  margin: 0;
  border-radius: 6px;
}

.connect-status-border {
  border-right: solid;
  border-width: 5px;
}

.online {
  border-color: lime;
}

.offline {
  border-color: red;
}

.device-list {
  padding: 0px 0;
}

.info-panel-details dt {
  float: left;
}

/* .work-detail-sheet {
  padding: 15px;
} */
</style>
